/**
 * BrandReveal — the quiet brand + product-family beat. Mounts during
 * beat 22 ("learn how to feel it"). The cue line at the top/middle
 * fades out first; then "suron" rises in; then the five service
 * badges (aether, constella, lyra, meridian, proxima) cascade in
 * beneath it. No canvas — pure DOM overlay on top of the scene.
 */
import { type CSSProperties, type ReactNode } from "react";
import { ALPHA, CREAM_RGB, cream, LABEL } from "./designTokens";

interface Props {
  /** Seconds the current beat has been active. */
  beatTime: number;
}

// Timings relative to beat start (seconds). Beat 22 is 11s.
const SURON_IN_AT = 3.8;
const BADGES_IN_AT = 5.4;
const BADGE_STAGGER = 0.22;

export function BrandReveal({ beatTime }: Props) {
  const suronP = smoothFade(beatTime, SURON_IN_AT, 1.4);

  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 9,
        pointerEvents: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {/* Suron wordmark — Inter 300, uppercase, soft outer glow. */}
      <div
        style={{
          fontFamily:
            "'Inter', ui-sans-serif, system-ui, -apple-system, 'Helvetica Neue', sans-serif",
          fontSize: "clamp(52px, 8vw, 104px)",
          fontWeight: 300,
          letterSpacing: "0.32em",
          textTransform: "uppercase",
          color: `rgba(${CREAM_RGB}, ${(0.98 * suronP).toFixed(3)})`,
          textShadow: `
            0 0 24px rgba(242, 238, 228, ${(0.25 * suronP).toFixed(3)}),
            0 0 56px rgba(242, 238, 228, ${(0.15 * suronP).toFixed(3)}),
            0 0 96px rgba(242, 238, 228, ${(0.08 * suronP).toFixed(3)})
          `,
          transform: `translateY(${(1 - suronP) * 14}px)`,
          filter: `blur(${(1 - suronP) * 8}px)`,
          marginBottom: "clamp(28px, 4vh, 56px)",
          paddingLeft: "0.32em", // optical balance for trailing letter-spacing
        }}
      >
        suron
      </div>

      {/* Five service badges */}
      <div
        style={{
          display: "flex",
          gap: "clamp(18px, 4.6vw, 72px)",
          alignItems: "flex-end",
          justifyContent: "center",
          paddingLeft: "clamp(12px, 3vw, 32px)",
          paddingRight: "clamp(12px, 3vw, 32px)",
          maxWidth: "100%",
        }}
      >
        {SERVICES.map((s, i) => {
          const start = BADGES_IN_AT + i * BADGE_STAGGER;
          const badgeP = smoothFade(beatTime, start, 1.1);
          return (
            <ServiceBadge
              key={s.name}
              name={s.name}
              logo={s.logo}
              progress={badgeP}
            />
          );
        })}
      </div>
    </div>
  );
}


function ServiceBadge({
  name,
  logo,
  progress,
}: {
  name: string;
  logo: ReactNode;
  progress: number;
}) {
  const lift = (1 - progress) * 14;
  const baseStyle: CSSProperties = {
    opacity: progress,
    transform: `translateY(${lift}px)`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 16,
    // Allow the badge column to compress on narrow viewports instead
    // of blowing out the row width with long labels like "CONSTELLA".
    minWidth: 0,
    flexShrink: 1,
  };
  return (
    <div style={baseStyle}>
      <div
        style={{
          width: "clamp(34px, 5.2vw, 72px)",
          height: "clamp(60px, 8.8vw, 124px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          opacity: 0.92,
        }}
      >
        {logo}
      </div>
      <span
        style={{
          fontFamily: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
          // Scales down from 13 px on desktop to ~8.5 px on iPhone SE
          // so nine-letter labels (CONSTELLA, MERIDIAN) don't bleed
          // past the logo column and push the whole row off-screen.
          fontSize: "clamp(8.5px, 1.3vw, 13px)",
          fontWeight: 500,
          letterSpacing: "clamp(1px, 0.35vw, 5px)",
          textTransform: "uppercase",
          textAlign: "center",
          whiteSpace: "nowrap",
          color: cream(ALPHA.labelActive * progress),
        }}
      >
        {name}
      </span>
    </div>
  );
}

/** Smooth 0→1 fade starting at `startSec`, reaching 1 over `durSec`. */
function smoothFade(t: number, startSec: number, durSec: number): number {
  if (t <= startSec) return 0;
  if (t >= startSec + durSec) return 1;
  const p = (t - startSec) / durSec;
  return p * p * (3 - 2 * p);
}

// ──────────────────────────────────────────────────────────────────────
// Service definitions — logos inlined so no asset pipeline is needed.
// Keep these in sync with resources/icons/*.svg.
// ──────────────────────────────────────────────────────────────────────

const FILL = `rgb(${CREAM_RGB})`;

function AetherMark() {
  return (
    <svg viewBox="0 0 140 240" width="100%" height="100%" aria-label="Aether">
      <g fill={FILL}>
        <rect x="10" y="96" width="20" height="144" />
        <rect x="60" y="0" width="20" height="240" />
        <rect x="110" y="96" width="20" height="144" />
      </g>
    </svg>
  );
}

function ConstellaMark() {
  return (
    <svg viewBox="0 0 140 240" width="100%" height="100%" aria-label="Constella">
      <g fill={FILL}>
        <rect x="0" y="0" width="140" height="20" />
        <rect x="0" y="110" width="56" height="20" />
        <rect x="0" y="220" width="140" height="20" />
      </g>
    </svg>
  );
}

function LyraMark() {
  return (
    <svg viewBox="0 0 90 240" width="100%" height="100%" aria-label="Lyra">
      <g fill={FILL}>
        <rect x="10" y="0" width="20" height="240" />
        <rect x="60" y="180" width="20" height="60" />
      </g>
    </svg>
  );
}

function MeridianMark() {
  return (
    <svg viewBox="0 0 140 240" width="100%" height="100%" aria-label="Meridian">
      <g fill={FILL}>
        <rect x="10" y="0" width="20" height="240" />
        <rect x="60" y="0" width="20" height="144" />
        <rect x="110" y="0" width="20" height="240" />
      </g>
    </svg>
  );
}

function ProximaMark() {
  return (
    <svg viewBox="0 0 90 240" width="100%" height="100%" aria-label="Proxima">
      <g fill={FILL}>
        <rect x="10" y="0" width="20" height="240" />
        <rect x="60" y="0" width="20" height="120" />
      </g>
    </svg>
  );
}

interface Service {
  name: string;
  logo: ReactNode;
}

const SERVICES: Service[] = [
  { name: "aether", logo: <AetherMark /> },
  { name: "constella", logo: <ConstellaMark /> },
  { name: "lyra", logo: <LyraMark /> },
  { name: "meridian", logo: <MeridianMark /> },
  { name: "proxima", logo: <ProximaMark /> },
];
